// packages/data/src/api/deal-fields.ts
//
// API functions for custom deal field CRUD and option management.
// All endpoints are under /api/deal_fields and gated by system_deal_fields feature flag.

import { z } from 'zod'
import { DealFieldListResponseSchema, DealFieldSchema, SelectOptionSchema } from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'
import type { DealFieldType, DealFieldState } from '../types'

// Backend wraps single-instance responses under the model's snake-cased name
// via ActiveModelJSONRenderer. Schemas below unwrap before exposing the payload.
const DealFieldResponseSchema = z.object({ deal_field: DealFieldSchema })
const SelectOptionResponseSchema = z.object({
  select_deal_field_option: SelectOptionSchema,
})

type CreateDealFieldPayload = {
  field_type: DealFieldType
  english_label: string
  french_label?: string
  state: DealFieldState
  config?: Record<string, unknown>
}

type UpdateDealFieldPayload = {
  english_label?: string
  french_label?: string
  state?: DealFieldState
  config?: Record<string, unknown>
}

export const dealFields = {
  async list(orgId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/organizations/${orgId}/deal_fields`)
    return parseWithZod(DealFieldListResponseSchema, result).deal_fields
  },

  async create(payload: CreateDealFieldPayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deal_fields`, { deal_field: payload })
    return parseWithZod(DealFieldResponseSchema, result).deal_field
  },

  async update(fieldId: number, payload: UpdateDealFieldPayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deal_fields/${fieldId}/update_field`, {
      deal_field: payload,
    })
    return parseWithZod(DealFieldResponseSchema, result).deal_field
  },

  async delete(fieldId: number) {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/deal_fields/${fieldId}`)
  },

  async addOption(fieldId: number, payload: { english_label: string; french_label?: string }) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deal_fields/${fieldId}/add_option`, payload)
    return parseWithZod(SelectOptionResponseSchema, result).select_deal_field_option
  },

  async updateOption(
    fieldId: number,
    payload: { option_id: number; english_label?: string; french_label?: string },
  ) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_fields/${fieldId}/update_option`,
      payload,
    )
    return parseWithZod(SelectOptionResponseSchema, result).select_deal_field_option
  },

  async removeOption(fieldId: number, payload: { option_id: number }) {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deal_fields/${fieldId}/remove_option`, payload)
  },

  async reorderOptions(fieldId: number, payload: { option_ids: number[] }) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_fields/${fieldId}/reorder_options`,
      payload,
    )
    return parseWithZod(DealFieldResponseSchema, result).deal_field
  },

  async reorderFields(payload: { field_set_id: number; field_ids: number[] }) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deal_fields/reorder_fields`, payload)
    return parseWithZod(DealFieldListResponseSchema, result).deal_fields
  },
}
